import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useWfSecurityService from 'src/api/wf/service/WfSecurityService';
import useWfUserService from 'src/api/wf/service/WfUserService';
import { FormRow } from 'src/ui/wf/common/form/FormRow';
import Input from 'src/ui/wf/common/input/Input';
import Button from 'src/ui/wf/common/button/Button';
import { MissingParameterError } from 'src/api/common/error/HttpError';
import { useWfErrorStore } from 'src/api/wf/stores/WfErrorStore';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

function WfLogin() {
  const { t } = useTranslation();
  const { wfLoginUser } = useWfSecurityService();
  const { wfGetUser } = useWfUserService();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showMissingUsername, setShowMissingUsername] = useState(false);
  const [showMissingPassword, setShowMissingPassword] = useState(false);
  const [underAuthentication, setUnderAuthentication] = useState(false);
  const [showAuthenticationError, setAuthenticationError] = useState(false);
  const { errors } = useWfErrorStore();
  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    const isUsernameEmpty = username.length === 0;
    const isPasswordEmpty = password.length === 0;
    setShowMissingUsername(isUsernameEmpty);
    setShowMissingPassword(isPasswordEmpty);

    if (isUsernameEmpty || isPasswordEmpty) {
      return;
    }

    setAuthenticationError(false);
    setUnderAuthentication(true);
    try {
      await wfLoginUser(username, password);
      await wfGetUser();
    } catch (err) {
      console.log('Login err', err);
    } finally {
      setUnderAuthentication(false);
    }
  };

  useEffect(() => {
    if (underAuthentication) {
      const hasLoginError = errors.some((error) => error instanceof MissingParameterError);

      if (hasLoginError) {
        setAuthenticationError(true);
      }
    }
  }, [errors, underAuthentication]);

  return (
    <div className="flex h-screen flex-col items-center justify-center bg-gray-100">
      <div className="w-full max-w-sm rounded bg-white p-8 shadow-md">
        <form onSubmit={handleSubmit}>
          <h2 className="mb-6 text-center text-2xl font-bold">{t('wf.common.title')}</h2>
          <div className="space-y-4">
            <FormRow showError={showMissingUsername} errorMessage={t('wf.login.missingUsername')}>
              <Input
                type="text"
                className="w-full rounded border p-2"
                placeholder={t('wf.login.ldapId')}
                value={username}
                onChange={(event) => {
                  setShowMissingUsername(false);
                  setUsername(event.target.value);
                }}
              />
            </FormRow>

            <FormRow showError={showMissingPassword} errorMessage={t('wf.login.missingPassword')}>
              <Input
                type="password"
                className="w-full rounded border p-2"
                placeholder={t('wf.login.password')}
                value={password}
                onChange={(event) => {
                  setShowMissingPassword(false);
                  setPassword(event.target.value);
                }}
              />
            </FormRow>

            {showAuthenticationError && (
              <div className="flex items-center justify-center text-sm text-red-500">
                <ExclamationCircleIcon className="mr-1 size-5" />
                <span>{t('wf.login.error')}</span>
              </div>
            )}
          </div>

          <div className="mt-6">
            <Button type="submit" title={t('wf.login.login')} className="w-full" />
          </div>
        </form>
      </div>
    </div>
  );
}

export default WfLogin;
